import React, { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { PlusOutlined, RobotOutlined } from "@ant-design/icons";
import { Button, Card, Drawer, Input, Segmented, Spin, Typography } from "antd";
import { useSearchParams } from "react-router-dom";

import { PermissionKey, PermissionScope, SupportRequestStatus } from "@fitness-app/data-models";

import { usePermission } from "~/hooks/usePermission";
import { useUserRole } from "~/hooks/trainer/useUserRole";

import { BoardColumn } from "./components/BoardColumn";
import { StatusChangeCommentModal, type PendingStatusChange } from "./components/StatusChangeCommentModal";
import { SupportAgentChat } from "./components/SupportAgentChat";
import { SupportRequestCard } from "./components/SupportRequestCard";
import { SupportRequestDrawer } from "./components/SupportRequestDrawer";
import { SupportRequestFormModal } from "./components/SupportRequestFormModal";
import { SupportSettings } from "./components/SupportSettings";
import { useSupportRequests } from "./hooks/useSupportRequests";
import { useSupportUnreadNotifications } from "./hooks/useSupportUnreadNotifications";
import { useUpdateSupportStatus } from "./hooks/useSupportApi";
import { BOARD_COLUMNS, CLOSED_SUPPORT_STATUSES, type SupportRequestWithRelations } from "./supportConstants";

const SupportBoard = (): React.ReactElement => {
  const { requests, isLoading } = useSupportRequests();
  const updateStatus = useUpdateSupportStatus();
  const { userId } = useUserRole();
  const canManageSupport = usePermission(PermissionKey.SUPPORT_MANAGE);
  const canReadSupport = usePermission(PermissionKey.SUPPORT_READ);
  const canUseAgents = usePermission(PermissionKey.AGENTS_USE);
  const canManageSupportResources = usePermission(PermissionKey.SUPPORT_RESOURCES_MANAGE, PermissionScope.ALL);
  const canUseSupportAgent = canManageSupport && canReadSupport && canUseAgents;
  const [view, setView] = useState<"board" | "settings">("board");
  const [activeRequest, setActiveRequest] = useState<SupportRequestWithRelations | null>(null);
  // Drop on Done/Rejected pauses for the closing-note modal (TRE-1765 #4);
  // cancel = the card never moves.
  const [pendingStatusChange, setPendingStatusChange] = useState<PendingStatusChange | null>(null);
  // The open drawer lives in the URL (?requestId=) so bell notifications and push
  // deep links can land directly on a request (TRE-1550).
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRequestId = searchParams.get("requestId");
  const [formOpen, setFormOpen] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState<"all" | "mine">("all");
  const { unreadRequestIds, markRequestNotificationsAsRead } = useSupportUnreadNotifications();

  // Opening a card is "I saw it" — clear its badge (and the bell rows) right away.
  // Re-runs when a new notification lands while the drawer is already open.
  useEffect(() => {
    if (selectedRequestId && unreadRequestIds.has(selectedRequestId)) {
      void markRequestNotificationsAsRead(selectedRequestId);
    }
  }, [selectedRequestId, unreadRequestIds, markRequestNotificationsAsRead]);

  const setSelectedRequestId = (requestId: string | null) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (requestId) {
          next.set("requestId", requestId);
        } else {
          next.delete("requestId");
        }
        return next;
      },
      { replace: true },
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const filteredRequests = useMemo(() => {
    const term = search.trim().toLowerCase();
    // "Moje" = anything I own a side of: assigned to me OR reported by me.
    const scoped =
      scope === "mine"
        ? requests.filter((request) => request.assigneeUserId === userId || request.reporterUserId === userId)
        : requests;
    if (!term) {
      return scoped;
    }
    return scoped.filter((request) =>
      [
        request.title,
        request.description,
        request.clientEmail,
        request.linearIssueIdentifier,
        request.trainee ? `${request.trainee.firstName} ${request.trainee.lastName} ${request.trainee.email}` : null,
      ]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(term)),
    );
  }, [requests, search, scope, userId]);

  const requestsByStatus = useMemo(() => {
    const grouped: Record<string, SupportRequestWithRelations[]> = Object.fromEntries(
      BOARD_COLUMNS.map((status) => [status, []]),
    );
    for (const request of filteredRequests) {
      (grouped[request.status] ?? grouped[SupportRequestStatus.Reported])!.push(request);
    }
    return grouped;
  }, [filteredRequests]);

  // Keep the drawer bound to fresh query data (status changes, new comments etc.).
  const selectedRequest = useMemo(
    () => requests.find((request) => request.id === selectedRequestId) || null,
    [requests, selectedRequestId],
  );

  const handleDragStart = (event: DragStartEvent) => {
    const request = event.active.data.current?.request as SupportRequestWithRelations | undefined;
    if (request) {
      setActiveRequest(request);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveRequest(null);

    if (!canManageSupport || !over) {
      return;
    }

    const request = active.data.current?.request as SupportRequestWithRelations | undefined;
    const targetStatus = over.data.current?.status as SupportRequestStatus | undefined;

    if (!request || !targetStatus || request.status === targetStatus) {
      return;
    }

    if (CLOSED_SUPPORT_STATUSES.includes(targetStatus)) {
      setPendingStatusChange({ requestId: request.id, displayId: request.displayId, status: targetStatus });
      return;
    }

    updateStatus.mutate({ id: request.id, status: targetStatus });
  };

  return (
    <Card
      title={<Typography.Title level={4}>Zgłoszenia</Typography.Title>}
      tabList={
        canManageSupportResources
          ? [
              { key: "board", tab: "Board" },
              { key: "settings", tab: "Ustawienia" },
            ]
          : undefined
      }
      activeTabKey={canManageSupportResources ? view : undefined}
      onTabChange={(key) => setView(key as "board" | "settings")}
      extra={
        view === "board" ? (
          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              value={scope}
              onChange={(value) => setScope(value as "all" | "mine")}
              options={[
                { value: "all", label: "Wszystkie" },
                { value: "mine", label: "Moje" },
              ]}
            />
            <Input.Search
              allowClear
              placeholder="Szukaj zgłoszenia..."
              onChange={(event) => setSearch(event.target.value)}
              className="w-full sm:w-64"
            />
            {canUseSupportAgent && (
              <Button type="primary" icon={<RobotOutlined />} onClick={() => setAgentOpen(true)}>
                Zgłoś z agentem
              </Button>
            )}
            {canManageSupport && (
              <Button icon={<PlusOutlined />} onClick={() => setFormOpen(true)}>
                Dodaj ręcznie
              </Button>
            )}
          </div>
        ) : undefined
      }
      className="min-h-full"
    >
      {canManageSupportResources && view === "settings" ? (
        <SupportSettings />
      ) : isLoading ? (
        <div className="flex min-h-[320px] items-center justify-center">
          <Spin />
        </div>
      ) : (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="flex gap-3 overflow-x-auto pb-4">
            {BOARD_COLUMNS.map((status) => (
              <BoardColumn
                key={status}
                status={status}
                requests={requestsByStatus[status] || []}
                unreadRequestIds={unreadRequestIds}
                onCardClick={(request) => setSelectedRequestId(request.id)}
              />
            ))}
          </div>
          <DragOverlay>
            {activeRequest && (
              <SupportRequestCard request={activeRequest} hasUnread={unreadRequestIds.has(activeRequest.id)} />
            )}
          </DragOverlay>
        </DndContext>
      )}

      <SupportRequestDrawer request={selectedRequest} onClose={() => setSelectedRequestId(null)} />
      <StatusChangeCommentModal
        pending={pendingStatusChange}
        onCancel={() => setPendingStatusChange(null)}
        onSubmit={(comment) => {
          if (pendingStatusChange) {
            updateStatus.mutate({ id: pendingStatusChange.requestId, status: pendingStatusChange.status, comment });
          }
          setPendingStatusChange(null);
        }}
      />
      <SupportRequestFormModal open={formOpen} onClose={() => setFormOpen(false)} />
      <Drawer
        title="Zgłoś problem"
        open={agentOpen}
        onClose={() => setAgentOpen(false)}
        width={Math.min(760, typeof window !== "undefined" ? window.innerWidth - 48 : 760)}
        destroyOnClose={false}
        styles={{ body: { display: "flex", flexDirection: "column", height: "100%" } }}
      >
        <SupportAgentChat />
      </Drawer>
    </Card>
  );
};

export default SupportBoard;
